import { useTranslation } from "react-i18next"

import TiktokConnectCta from "@/components/social-accounts/tiktok-connect-cta"
import TiktokIcon from "@/components/social-accounts/tiktok-icon"
import TiktokOAuthStartForm from "@/components/social-accounts/tiktok-oauth-start-form"
import { Card, CardContent } from "@/components/ui/card"

/**
 * OKS-734 — Not-connected state (no `tiktok_connection` row).
 *
 * Replaces the ENTIRE content area (no chart shell) with a centered
 * prompt to connect the brand's TikTok. The CTA is the shared
 * `TiktokConnectCta` inside the NATIVE `TiktokOAuthStartForm` — cross-
 * origin OAuth cannot ride an Inertia XHR (see the form's JSDoc). The
 * POST URL is the brand connect action, passed as `reconnectUrl`.
 */
export interface ChannelNotConnectedCardProps {
  reconnectUrl: string
}

export function ChannelNotConnectedCard({
  reconnectUrl,
}: ChannelNotConnectedCardProps) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid="channel-analytics-not-connected"
      className="mx-auto flex max-w-md flex-col items-center gap-4 p-8 text-center"
    >
      <CardContent className="flex flex-col items-center gap-4 p-0">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full bg-black"
          aria-hidden
        >
          <TiktokIcon width={24} height={24} />
        </span>
        <h2 className="text-foreground text-lg font-semibold">
          {t("channel.not_connected.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("channel.not_connected.body")}
        </p>
        <TiktokOAuthStartForm
          action={reconnectUrl}
          data-testid="channel-analytics-connect-form"
          className="w-full"
        >
          <TiktokConnectCta
            labelKey="channel.not_connected.cta"
            data-testid="channel-analytics-connect-cta"
          />
        </TiktokOAuthStartForm>
      </CardContent>
    </Card>
  )
}
